import { campaignBudgetMin, validateCampaignBudget } from '@/utils/campaignCreate';

export type HourlyBudgetTimeWindowDraft = {
  clientId: string;
  id?: string;
  start_time_local: string;
  end_time_local: string;
  multiplier: string;
  days_of_week?: number[];
};

export type HourlyBudgetBaseWindowDraft = {
  clientId: string;
  id?: string;
  start_time_local: string;
  end_time_local: string;
  days_of_week?: number[];
};

const HHMM_RE = /^(\d{1,2}):(\d{2})$/;
const MAX_WINDOWS = 12;

/** Sponsored Products daily minimum for the open account, when the marketplace is known. */
export function baseBudgetMin(): number | undefined {
  return campaignBudgetMin('SP') ?? undefined;
}

export function parseBaseBudgetForPayload(
  input: string
): { value: number | null; error?: string } {
  const trimmed = input.trim();
  if (!trimmed) {
    return { value: null };
  }
  const parsed = parseFloat(trimmed);
  const budgetError = validateCampaignBudget(parsed, 'SP', 'daily', 'Base budget');
  if (Number.isNaN(parsed) || budgetError) {
    return {
      value: null,
      error: budgetError ?? 'Base budget must be a valid number.',
    };
  }
  return { value: parsed };
}

/** Hour-only schedule: minutes are always :00. */
export const IST_HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  hour,
  value: `${String(hour).padStart(2, '0')}:00`,
  label: formatIstHourLabel(hour),
  shortLabel: formatIstHourShortLabel(hour),
}));

export function formatIstHourLabel(hour: number): string {
  const h = ((hour % 24) + 24) % 24;
  if (h === 0) return '12:00 AM';
  if (h < 12) return `${h}:00 AM`;
  if (h === 12) return '12:00 PM';
  return `${h - 12}:00 PM`;
}

export function formatIstHourShortLabel(hour: number): string {
  const h = ((hour % 24) + 24) % 24;
  if (h === 0) return '12a';
  if (h < 12) return `${h}a`;
  if (h === 12) return '12p';
  return `${h - 12}p`;
}

export function hourFromTimeLocal(value: string): number {
  const normalized = normalizeTimeLocal(value);
  return Number(normalized.slice(0, 2));
}

/** Valid API TIME for exclusive midnight. Hour 24 is not a legal clock value. */
export const END_OF_DAY_TIME_LOCAL = '23:59';

/**
 * Exclusive end hour for a half-open [start, end) window.
 * 24 is an internal bound only (end of day), never an API hour.
 */
export function exclusiveEndHourFromTimeLocal(endTimeLocal: string, startHour: number): number {
  const s = String(endTimeLocal ?? '').trim();
  const m = /^(\d{1,2})(?::(\d{2}))?/.exec(s);
  if (!m) return hourFromTimeLocal(endTimeLocal);
  const hour = Number(m[1]);
  const minute = m[2] != null ? Number(m[2]) : 0;
  if (hour >= 24) return 24;
  if (hour === 23 && minute >= 59) return 24;
  if (hour === 0 && minute === 0 && startHour > 0) return 24;
  if (minute > 0) return Math.min(24, hour + 1);
  return Math.min(23, Math.max(0, Number.isNaN(hour) ? 0 : hour));
}

/** Map an exclusive end hour (1–24) to an API TIME in 00:00–23:59. */
export function exclusiveEndTimeLocalFromHour(exclusiveEndHour: number): string {
  if (exclusiveEndHour >= 24) return END_OF_DAY_TIME_LOCAL;
  return timeLocalFromHour(exclusiveEndHour);
}

export function timeLocalFromHour(hour: number | string): string {
  const h = typeof hour === 'string' ? parseInt(hour, 10) : hour;
  const clamped = Math.min(23, Math.max(0, Number.isNaN(h) ? 0 : h));
  return `${String(clamped).padStart(2, '0')}:00`;
}

export function parseHhmm(value: string): { ok: true; minutes: number } | { ok: false; error: string } {
  const s = (value || '').trim();
  const m = HHMM_RE.exec(s);
  if (!m) return { ok: false, error: `Invalid time "${value}"; use HH:MM (24h)` };
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour > 23 || minute > 59) return { ok: false, error: `Invalid time "${value}"` };
  return { ok: true, minutes: hour * 60 + minute };
}

function windowIntervals(startMin: number, endMin: number): [number, number][] {
  if (endMin <= startMin) return [];
  return [[startMin, endMin]];
}

function intervalsOverlap(a: [number, number], b: [number, number]): boolean {
  return a[0] < b[1] && b[0] < a[1];
}

function daysOf(row: { days_of_week?: number[] }): number[] {
  const days = row.days_of_week;
  if (!days || days.length === 0) return [0, 1, 2, 3, 4, 5, 6];
  return days;
}

function daysIntersect(a: number[], b: number[]): boolean {
  return a.some((day) => b.includes(day));
}

function windowContainedInBase(
  childStart: number,
  childEnd: number,
  baseStart: number,
  baseEnd: number
): boolean {
  return childStart >= baseStart && childEnd <= baseEnd;
}

export function validateBaseWindows(
  rows: HourlyBudgetBaseWindowDraft[]
): { ok: true } | { ok: false; errors: string[] } {
  const errors: string[] = [];
  if (rows.length > MAX_WINDOWS * 7) {
    return { ok: false, errors: [`At most ${MAX_WINDOWS} base windows allowed per weekday`, ...errors] };
  }

  const parsed: { index: number; start: number; end: number; days: number[] }[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const start = parseHhmm(row.start_time_local);
    const end = parseHhmm(row.end_time_local);
    if (!start.ok) {
      errors.push(`Base window ${i + 1}: ${start.error}`);
      continue;
    }
    if (!end.ok) {
      errors.push(`Base window ${i + 1}: ${end.error}`);
      continue;
    }
    if (end.minutes <= start.minutes) {
      errors.push(`Base window ${i + 1}: End hour must be after start hour`);
      continue;
    }
    parsed.push({ index: i, start: start.minutes, end: end.minutes, days: daysOf(row) });
  }

  const perDayCounts = new Map<number, number>();
  for (const row of parsed) {
    for (const day of row.days) {
      perDayCounts.set(day, (perDayCounts.get(day) ?? 0) + 1);
    }
  }
  for (const count of perDayCounts.values()) {
    if (count > MAX_WINDOWS) {
      errors.push(`At most ${MAX_WINDOWS} base windows allowed per weekday`);
      break;
    }
  }

  for (let a = 0; a < parsed.length; a++) {
    for (let b = a + 1; b < parsed.length; b++) {
      const wa = parsed[a];
      const wb = parsed[b];
      if (!daysIntersect(wa.days, wb.days)) continue;
      const ia = windowIntervals(wa.start, wa.end);
      const ib = windowIntervals(wb.start, wb.end);
      for (const segA of ia) {
        for (const segB of ib) {
          if (intervalsOverlap(segA, segB)) {
            errors.push(`Base windows ${wa.index + 1} and ${wb.index + 1} overlap`);
          }
        }
      }
    }
  }

  return errors.length ? { ok: false, errors } : { ok: true };
}

export function clampWindowToBaseWindows(
  row: HourlyBudgetTimeWindowDraft,
  baseWindows: HourlyBudgetBaseWindowDraft[]
): HourlyBudgetTimeWindowDraft {
  if (baseWindows.length === 0) return row;

  let startHour = hourFromTimeLocal(row.start_time_local);
  let endHour = hourFromTimeLocal(row.end_time_local);

  const containing = baseWindows
    .map((bw) => {
      const bs = hourFromTimeLocal(bw.start_time_local);
      const be = hourFromTimeLocal(bw.end_time_local);
      return { bs, be };
    })
    .find(({ bs, be }) => startHour >= bs && endHour <= be);

  const target = containing ?? {
    bs: hourFromTimeLocal(baseWindows[0].start_time_local),
    be: hourFromTimeLocal(baseWindows[0].end_time_local),
  };

  startHour = Math.max(target.bs, Math.min(startHour, target.be - 1));
  endHour = Math.max(startHour + 1, Math.min(endHour, target.be));

  return {
    ...row,
    start_time_local: timeLocalFromHour(startHour),
    end_time_local: timeLocalFromHour(endHour),
  };
}

export function validateTimeWindows(
  rows: HourlyBudgetTimeWindowDraft[],
  baseWindows?: HourlyBudgetBaseWindowDraft[]
): { ok: true } | { ok: false; errors: string[] } {
  const errors: string[] = [];
  if (baseWindows && baseWindows.length > 0) {
    const baseCheck = validateBaseWindows(baseWindows);
    if (!baseCheck.ok) {
      errors.push(...baseCheck.errors);
    }
  }
  if (rows.length > MAX_WINDOWS * 7) {
    return { ok: false, errors: [`At most ${MAX_WINDOWS} time windows allowed per weekday`, ...errors] };
  }

  const parsed: { index: number; start: number; end: number; days: number[] }[] = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const start = parseHhmm(row.start_time_local);
    const end = parseHhmm(row.end_time_local);
    if (!start.ok) {
      errors.push(`Row ${i + 1}: ${start.error}`);
      continue;
    }
    if (!end.ok) {
      errors.push(`Row ${i + 1}: ${end.error}`);
      continue;
    }
    if (end.minutes <= start.minutes) {
      errors.push(`Row ${i + 1}: End hour must be after start hour`);
      continue;
    }
    const childDays = daysOf(row);
    if (baseWindows && baseWindows.length > 0) {
      const contained = childDays.every((day) =>
        baseWindows.some((bw) => {
          if (!daysOf(bw).includes(day)) return false;
          const bs = parseHhmm(bw.start_time_local);
          const be = parseHhmm(bw.end_time_local);
          if (!bs.ok || !be.ok) return false;
          return windowContainedInBase(start.minutes, end.minutes, bs.minutes, be.minutes);
        })
      );
      if (!contained) {
        errors.push(`Row ${i + 1}: Time window must be within a base operating window`);
        continue;
      }
    }
    const mult = parseFloat(row.multiplier);
    if (Number.isNaN(mult) || mult <= 0) {
      errors.push(`Row ${i + 1}: multiplier must be greater than 0`);
      continue;
    }
    parsed.push({ index: i, start: start.minutes, end: end.minutes, days: childDays });
  }

  for (let a = 0; a < parsed.length; a++) {
    for (let b = a + 1; b < parsed.length; b++) {
      const wa = parsed[a];
      const wb = parsed[b];
      if (!daysIntersect(wa.days, wb.days)) continue;
      const ia = windowIntervals(wa.start, wa.end);
      const ib = windowIntervals(wb.start, wb.end);
      for (const segA of ia) {
        for (const segB of ib) {
          if (intervalsOverlap(segA, segB)) {
            errors.push(`Rows ${wa.index + 1} and ${wb.index + 1} overlap`);
          }
        }
      }
    }
  }

  return errors.length ? { ok: false, errors } : { ok: true };
}

export function newBaseWindowDraft(
  existing: HourlyBudgetBaseWindowDraft[] = []
): HourlyBudgetBaseWindowDraft {
  const used = new Set<number>();
  for (const row of existing) {
    const s = hourFromTimeLocal(row.start_time_local);
    const e = hourFromTimeLocal(row.end_time_local);
    for (let h = s; h < e; h++) used.add(h);
  }
  let start = 9;
  while (start < 23 && used.has(start)) start++;
  const end = Math.min(23, start + 3);
  return {
    clientId: crypto.randomUUID(),
    start_time_local: timeLocalFromHour(start),
    end_time_local: timeLocalFromHour(end),
  };
}

export function newWindowDraft(
  baseWindows?: HourlyBudgetBaseWindowDraft[]
): HourlyBudgetTimeWindowDraft {
  if (baseWindows && baseWindows.length > 0) {
    const bw = baseWindows[0];
    const start = hourFromTimeLocal(bw.start_time_local);
    const end = hourFromTimeLocal(bw.end_time_local);
    const endClamped = Math.min(23, Math.max(start + 1, Math.min(start + 3, end)));
    return {
      clientId: crypto.randomUUID(),
      start_time_local: timeLocalFromHour(start),
      end_time_local: timeLocalFromHour(endClamped),
      multiplier: '1',
    };
  }
  return {
    clientId: crypto.randomUUID(),
    start_time_local: '00:00',
    end_time_local: '08:00',
    multiplier: '1',
  };
}

/** Normalize API TIME strings. 23:59 is exclusive midnight; 24:00 is rewritten to 23:59. */
export function normalizeTimeLocal(value: unknown): string {
  const s = String(value ?? '').trim();
  const m = /^(\d{1,2})(?::(\d{2}))?/.exec(s);
  if (!m) return '00:00';
  const hour = Number(m[1]);
  const minute = m[2] != null ? Number(m[2]) : 0;
  if (hour >= 24 || (hour === 23 && minute >= 59)) return END_OF_DAY_TIME_LOCAL;
  return timeLocalFromHour(hour);
}

export function baseWindowsFromApi(row: Record<string, unknown>): HourlyBudgetBaseWindowDraft[] {
  const raw = row.base_windows;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((item, i) => {
      const r = item as Record<string, unknown>;
      return {
        clientId: typeof r.id === 'string' ? r.id : `base-${i}`,
        id: typeof r.id === 'string' ? r.id : undefined,
        start_time_local: normalizeTimeLocal(r.start_time_local),
        end_time_local: normalizeTimeLocal(r.end_time_local),
        days_of_week: Array.isArray(r.days_of_week) ? r.days_of_week.map(Number) : undefined,
      };
    });
  }
  const start = row.start_time;
  const end = row.end_time;
  if (start != null && start !== '' && end != null && end !== '') {
    return [
      {
        clientId: 'legacy-base',
        start_time_local: normalizeTimeLocal(start),
        end_time_local: normalizeTimeLocal(end),
      },
    ];
  }
  return [];
}

export function windowsFromApi(raw: unknown): HourlyBudgetTimeWindowDraft[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item, i) => {
    const row = item as Record<string, unknown>;
    return {
      clientId: typeof row.id === 'string' ? row.id : `row-${i}`,
      id: typeof row.id === 'string' ? row.id : undefined,
      start_time_local: normalizeTimeLocal(row.start_time_local),
      end_time_local: normalizeTimeLocal(row.end_time_local),
      multiplier: String(row.multiplier ?? '1'),
      days_of_week: Array.isArray(row.days_of_week) ? row.days_of_week.map(Number) : undefined,
    };
  });
}

/** PUT may return `{ success, data }` or the budget object directly. */
export function hourlyBudgetFromApiResponse(data: unknown): Record<string, unknown> | null {
  if (!data || typeof data !== 'object') return null;
  const obj = data as Record<string, unknown>;
  if (
    obj.hourly_budget != null ||
    obj.schedule_mode != null ||
    Array.isArray(obj.time_windows) ||
    Array.isArray(obj.base_windows) ||
    Array.isArray(obj.day_settings)
  ) {
    return obj;
  }
  if (obj.data && typeof obj.data === 'object' && obj.data !== null) {
    return obj.data as Record<string, unknown>;
  }
  return null;
}

export function baseWindowsToPayload(rows: HourlyBudgetBaseWindowDraft[]) {
  return rows.map((row, i) => ({
    start_time_local: normalizeTimeLocal(row.start_time_local),
    end_time_local: normalizeTimeLocal(row.end_time_local),
    sort_order: i,
    ...(row.days_of_week && row.days_of_week.length > 0 ? { days_of_week: row.days_of_week } : {}),
  }));
}

export function windowsToPayload(rows: HourlyBudgetTimeWindowDraft[]) {
  return rows.map((row, i) => ({
    start_time_local: normalizeTimeLocal(row.start_time_local),
    end_time_local: normalizeTimeLocal(row.end_time_local),
    multiplier: parseFloat(row.multiplier),
    sort_order: i,
    ...(row.days_of_week && row.days_of_week.length > 0 ? { days_of_week: row.days_of_week } : {}),
  }));
}

/** Min/max hour bounds for multiplier UI when base windows exist. */
export function baseWindowHourBounds(
  baseWindows: HourlyBudgetBaseWindowDraft[],
  rowStartHour: number
): { minStart?: number; maxStart?: number; minEnd?: number; maxEnd?: number } {
  if (baseWindows.length === 0) return {};
  const containing = baseWindows
    .map((bw) => ({
      start: hourFromTimeLocal(bw.start_time_local),
      end: hourFromTimeLocal(bw.end_time_local),
    }))
    .find((b) => rowStartHour >= b.start && rowStartHour < b.end);
  const b = containing ?? {
    start: hourFromTimeLocal(baseWindows[0].start_time_local),
    end: hourFromTimeLocal(baseWindows[0].end_time_local),
  };
  return {
    minStart: b.start,
    maxStart: Math.max(b.start, b.end - 1),
    minEnd: rowStartHour + 1,
    maxEnd: b.end,
  };
}
